import { faBell, faCheckSquare, faSquare } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import React from "react";

import ContestItem, { ContestItemPlaceholder } from "@/mk2/apps/contests/components/ContestItem";
import { READ_FEED_TIMEOUT } from "@/mk2/apps/forum/constants";
import { ContestEntity } from "@/mk2/apps/forum/schemas";
import { wallUrl } from "@/mk2/apps/wall/urls";
import { InfiniteListLoadable } from "@/mk2/components/InfiniteListLoadable";
import { Interpolate } from "@/mk2/components/Interpolate";
import { Link } from "@/mk2/components/Link";
import { ListHeadingItem } from "@/mk2/components/ListHeadingItem";
import { Panel } from "@/mk2/components/Panel";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    CONTESTS_I_WANT_TO_RECEIVE_notifications,
    CONTESTS_WITH_AMBASSADORS,
    CONTESTS_WITH_MK,
    NOTIFICATIONS,
    TOPIC_CONTESTS_ENDED_TITLE,
    TOPIC_CONTESTS_NO_RUNNING,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

import styles from "./ContestsHome.mscss";
import { Props } from "./ContestsHomePage";

const ROW_HEIGHT_MOBILE = 60;
const ROW_HEIGHT_DESKTOP = 60;

class ContestsHome extends React.Component<Props> {
    private readTimer: number;

    public componentDidMount() {
        if (this.props.unreadCount) {
            this.readTimer = window.setTimeout(this.props.onRead, READ_FEED_TIMEOUT);
        }
    }

    public componentDidUpdate(prevProps: Props) {
        if (this.props.unreadCount) {
            window.clearTimeout(this.readTimer);
            this.readTimer = window.setTimeout(this.props.onRead, READ_FEED_TIMEOUT);
        }
    }

    public componentWillUnmount() {
        window.clearTimeout(this.readTimer);
    }

    public render() {
        const { finishedContests, loadingState } = this.props;
        return (
            <div className={styles.ContestsHome}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderInit={this.renderInit}
                    onRenderSuccess={this.renderSuccess}
                    hasData={finishedContests && finishedContests.length > 0}
                />
            </div>
        );
    }

    private renderInit = () => {
        const { isMobile } = this.props;

        return (
            <div className={styles.ContestsHome}>
                <ListHeadingItem className={styles.ContestsHome__heading}>
                    {TOPIC_CONTESTS_ENDED_TITLE}
                </ListHeadingItem>
                <div className={styles.ContestsHome__items}>
                    <ContestItemPlaceholder isMobile={isMobile} />
                    <ContestItemPlaceholder isMobile={isMobile} />
                    <ContestItemPlaceholder isMobile={isMobile} />
                    <ContestItemPlaceholder isMobile={isMobile} />
                    <ContestItemPlaceholder isMobile={isMobile} />
                    <ContestItemPlaceholder isMobile={isMobile} />
                </div>
            </div>
        );
    };

    private renderSuccess = () => {
        const {
            draftContests,
            finishedContests,
            hasMore,
            isFollowing,
            isMobile,
            requestUser,
            runningAmbContests,
            runningMkContests,
        } = this.props;

        return (
            <div className={styles.ContestsHome}>
                {isAuthenticated(requestUser) && (
                    <Panel className={styles.ContestsHome__notifs}>
                        <label
                            className={styles.ContestsHome__notifs__label}
                            htmlFor="wannaReceiveNotifs"
                        >
                            <div className={styles.ContestsHome__notifs__label__icon}>
                                <FontAwesomeIcon icon={isFollowing ? faCheckSquare : faSquare} />
                            </div>
                            <input
                                checked={isFollowing}
                                className={styles.ContestsHome__notifs__label__input}
                                id="wannaReceiveNotifs"
                                type="checkbox"
                                onChange={this.handleSetFollowingChange}
                            />
                            <Interpolate
                                i18nKey={CONTESTS_I_WANT_TO_RECEIVE_notifications}
                                notifications={
                                    <Link to={wallUrl}>
                                        <FontAwesomeIcon icon={faBell} />{" "}
                                        {`${NOTIFICATIONS.toLocaleLowerCase()}`}
                                    </Link>
                                }
                            />
                        </label>
                    </Panel>
                )}

                {draftContests?.length > 0 && [
                    <ListHeadingItem key="drafts" className={styles.ContestsHome__heading}>
                        <span className={styles.ContestsHome__admin}>Drafts</span>
                    </ListHeadingItem>,

                    draftContests.map((contest) => {
                        return (
                            <ContestItem
                                contest={contest}
                                isMobile={isMobile}
                                key={contest.id}
                                className={cx(
                                    styles.ContestsHome__admin,
                                    styles.ContestsHome__contestItemWithVisitedColor,
                                )}
                            />
                        );
                    }),
                ]}

                {runningMkContests.length > 0 && [
                    <ListHeadingItem key="runningMk" className={styles.ContestsHome__heading}>
                        {CONTESTS_WITH_MK}
                    </ListHeadingItem>,

                    runningMkContests.map((contest) => {
                        return (
                            <ContestItem
                                contest={contest}
                                isMobile={isMobile}
                                key={contest.id}
                                className={styles.ContestsHome__contestItemWithVisitedColor}
                            />
                        );
                    }),
                ]}

                {runningAmbContests.length > 0 && [
                    <ListHeadingItem key="runningAmb" className={styles.ContestsHome__heading}>
                        {CONTESTS_WITH_AMBASSADORS}
                    </ListHeadingItem>,

                    runningAmbContests.map((contest) => {
                        return (
                            <ContestItem
                                contest={contest}
                                isMobile={isMobile}
                                key={contest.id}
                                className={styles.ContestsHome__contestItemWithVisitedColor}
                            />
                        );
                    }),
                ]}

                {runningMkContests.length === 0 && runningAmbContests.length === 0 && (
                    <div className={styles.ContestsHome__noRunningContest}>
                        {TOPIC_CONTESTS_NO_RUNNING}
                    </div>
                )}

                <ListHeadingItem className={styles.ContestsHome__heading}>
                    {TOPIC_CONTESTS_ENDED_TITLE}
                </ListHeadingItem>
                <div className={styles.ContestsHome__items}>
                    <InfiniteListLoadable
                        isRowLoaded={this.isRowLoaded}
                        loadMoreRows={this.loadNext}
                        rowCount={hasMore ? finishedContests.length + 1 : finishedContests.length}
                        rowHeight={isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP}
                        rowRenderer={(props) => this.rowRenderer(props)}
                    />
                </div>
            </div>
        );
    };

    private isRowLoaded = ({ index }) =>
        !this.props.hasMore || index < this.props.finishedContests.length;

    private rowRenderer = ({ index, key, style }) => {
        const { finishedContests, isMobile } = this.props;

        if (!this.isRowLoaded({ index })) {
            return (
                <ContestItemPlaceholder
                    key={key}
                    style={{ ...style, height: isMobile ? ROW_HEIGHT_MOBILE : ROW_HEIGHT_DESKTOP }}
                    isMobile={isMobile}
                />
            );
        }

        const contest: ContestEntity = finishedContests[index];

        return <ContestItem contest={contest} key={key} isMobile={isMobile} style={style} />;
    };

    private load = () => {
        const { onLoad, after } = this.props;

        // // Do not trigger next load
        // if (this.props.loadingState === LoadingState.LOADING) {
        //     return;
        // }

        onLoad(after);
    };

    private loadNext = () => {
        // Do not trigger next load
        if (this.props.loadingState === LoadingState.LOADING) {
            return;
        }

        const { finishedContests, onLoad } = this.props;

        let after = null;
        if (finishedContests.length) {
            after = finishedContests[finishedContests.length - 1].id;
        }
        onLoad(after);

        // Debounce loadMoreRows
        return new Promise((resolve) => {
            setTimeout(resolve, 500);
        });
    };

    private handleSetFollowingChange = (event: React.FormEvent<HTMLInputElement>) => {
        const { isFollowing, onSetFollowing, setFollowingLoadingState } = this.props;
        if (setFollowingLoadingState !== LoadingState.LOADING) {
            onSetFollowing(!isFollowing);
        } else {
            event.preventDefault();
        }
    };
}

export default ContestsHome;
